import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { errorLogger } from "@/Common/helpers/logger";
import { apiInstance, gql } from "@/services/httpAdapter";
import { IFinanceWashout } from "@/Washouts/types/Api/Finance";

// Common fragments
import {
	AddedCompensationFragment,
	CallActivityPerformanceFragment,
	ChargebacksFragment,
	CompensationFragment,
	EmployeeFragment,
	LongevityFragment,
	ReviewFragment,
	SalesPerformanceFragment,
} from "@/Washouts/api/common/fragments";
import { CancellationsFragment, SalesLogFragment } from "@/Washouts/api/Departments/finance/fragments";

type Params = string[];

export async function getFinanceWashout(...params: Params) {
	const [location, userId, commissionCycleId, payPeriodId, position] = params;
	const query = gql`
		query getFinanceWashout(
			$locationId: String!
			$userId: String!
			$commissionCycleId: String!
			$payPeriodId: String!
			$position: String!
		) {
			getFinanceWashout(
				locationId: $locationId
				userId: $userId
				commissionCycleId: $commissionCycleId
				payPeriodId: $payPeriodId
				position: $position
			) {
				_id
				userId
				commissionCycleId
				payPeriodId
				created
				overdraw
				employee {
					...Employee
				}
				salesPerformance {
					...SalesPerformance
				}
				carryOver {
					...SalesPerformance
				}
				longevity {
					...Longevity
				}
				addedCompensations {
					...AddedCompensation
				}
				summary {
					...Compensation
				}
				cancellations {
					...Cancellations
				}
				salesLog {
					...SalesLog
				}
				productConversions {
					new {
						products {
							_id
							penetration
							penetrationLabel
							product
							quantity
							contracts
							total
						}
						penetration
					}
					preOwned {
						products {
							_id
							penetration
							penetrationLabel
							product
							quantity
							contracts
							total
						}
						penetration
					}
					totals {
						products {
							_id
							penetration
							penetrationLabel
							product
							quantity
							contracts
							total
						}
						penetration
					}
					globalGrossAndUnits
				}
				newChargebacks {
					...Chargebacks
				}
				preOwnedChargebacks {
					...Chargebacks
				}
				deductions {
					deductions {
						_id
						title
						value
					}
					total
				}
				spiffs {
					spiffs {
						_id
						date
						spiffName
						spiffType
						amount
						stocksNumbers
					}
					total
				}
				performanceMetrics {
					performanceMetrics {
						_id
						backendPvr
						newBackendPvr
						preOwnedBackendPvr
						unitsAdjustments {
							cash
							lease
						}
						unfundedDeals
					}
					globalGrossAndUnits
				}
				citPerformance {
					cits {
						_id
						dealStatus
						fundedDealsAmount
						totalDealsAmount
						averageDaysInTransit
						individualEfficiency
						storeAverageDaysInTransit
						storeEfficiency
					}
					totals {
						fundedDealsAmount
						totalDealsAmount
						averageDaysInTransit
						individualEfficiency
						storeAverageDaysInTransit
						storeEfficiency
					}
				}
				review {
					...Review
				}
				callActivityPerformance {
					...WashoutCallActivityPerformance
				}
			}
		}
		${AddedCompensationFragment}
		${CallActivityPerformanceFragment}
		${CancellationsFragment}
		${ChargebacksFragment}
		${CompensationFragment}
		${EmployeeFragment}
		${LongevityFragment}
		${ReviewFragment}
		${SalesLogFragment}
		${SalesPerformanceFragment}
	`;

	const variables = {
		locationId: location,
		commissionCycleId,
		userId,
		payPeriodId,
		position,
	};

	try {
		const response: GraphqlQueryResponse<"getFinanceWashout", IFinanceWashout> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getFinanceWashout;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}

export async function getFinanceWashoutReview(...params: Params) {
	const [location, commissionCycle, payPeriodId = null, position] = params;
	const query = gql`
		query getFinanceWashout($locationId: String!, $commissionCycleId: String!, $payPeriodId: String, $position: String!) {
			getFinanceWashout(
				commissionCycleId: $commissionCycleId
				locationId: $locationId
				payPeriodId: $payPeriodId
				position: $position
			) {
				_id
				review {
					...Review
				}
			}
		}
		${ReviewFragment}
	`;
	const variables = {
		locationId: location,
		commissionCycleId: commissionCycle,
		payPeriodId,
		position,
	};

	try {
		const response: GraphqlQueryResponse<"getFinanceWashout", Pick<IFinanceWashout, "_id" | "review">> = await apiInstance.post(
			"/graphql",
			{
				query,
				variables,
			}
		);
		return response.data.data?.getFinanceWashout;
	} catch (error) {
		errorLogger(error as Error);
		return null;
	}
}
